import { CommonModule, Location } from '@angular/common';
import { BrisbaneDatePipe } from '../../../../shared/pipes/brisbane-date.pipe';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { HttpService } from '../../../../shared/services/http.service';
import { CustomToastComponent } from '../../../../shared/components/toast/custom-toast.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { tap, finalize } from 'rxjs';
import ApexCharts from 'apexcharts';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AddStudentComponent } from '../../../../shared/components/dialog/add-student/add-student.component';
import { AssignStudentsDialogComponent } from '../../../../shared/components/dialog/assign-students/assign-students-dialog.component';
import { AddEventComponent } from '../../../../shared/components/dialog/add-event/add-event.component';
import { TableComponent } from '../../../../shared/components/custom-table/table.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { SendEmailDialogComponent } from '../../../../shared/components/dialog/send-email/send-email-dialog.component';
import { EventLinkDialogComponent } from '../../../../shared/components/dialog/event-copy-link/event-copy-link-dialog.component';
import { AddFeedBackDialogComponent } from '../../../../shared/components/dialog/add-feedback/add-feedback-dialog.component';
import { CustomRegTableComponent } from '../../../../shared/components/custom-reg-table/custom-reg-table.component';
import { WorkExperienceComponent } from '../../../../shared/components/dialog/work-experience/work-experience.component';
import { UrlParserService } from '../../../../shared/services/url-parser.service';

interface TeacherDialogData {
  initialData?: APIResponse;
  EditMode: boolean;
  ViewMode: boolean;
  schoolID: string;
  eventId?: string;
  addStudent?: boolean;
}

interface APIResponse {
  status: boolean;
  message: string;
  data: any[] | { [key: string]: any };
}

@Component({
  selector: 'teacher-event',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CustomRegTableComponent, BrisbaneDatePipe],
  templateUrl: './teacher-event.component.html',
  styleUrls: ['./teacher-event.component.scss'],
})
export class TeacherEventComponent implements OnInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  private chart: ApexCharts | null = null;
  adminStudentColumn: string[] = [
    'student name',
    'media consent',
    'PD Points',
    'notes',
  ];
  teacheStudentColumn: string[] = [
    'student name',
    'status',
    'attended',
    'media consent',
    'notes',
  ];
  loginRole: any = '';
  eventData: any = null;
  pageloading: boolean = false;
  loading: boolean = false;
  approvalStatusLoading: boolean = false;
  eventID: any = '';
  showStatusDropdown = false;
  showApprovalStatusDropdown = false;
  statusOptions: any[] = [];
  chartData: { date: string; registrations: number }[] = [];
  chartInitialized: boolean = false;
  chartLoading: boolean = false;
  resChartData: any = null;
  engagementMetrics: any = null;
  eventStudentList: any[] = [];
  listViewRoles = {
    shareEmail: false,
    syncWithAsana: false,
    statusChange: false,
    edit: false,
    assignStudent: false,
    studentList: {
      edit: false,
      statusChange: false,
      mediaConsent: false,
    },
  };
  downloadInProgress: boolean = false;
  studentTabelLoading: boolean = false;
  isLoading: boolean = false;
  isFetchingStudentData: boolean = true;
  isRegistered: boolean = false;
  showConsentButton: boolean = false;
  downloadingRowId: string | null = null;
  showEventModal: any = '';

  private statusDropdownClickListener: (() => void) | null = null;
  private loadingTimeout: any;
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;

  constructor(
    private fb: FormBuilder,
    private location: Location,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private urlParser: UrlParserService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const encodedId = params.get('id');
      this.eventID = encodedId ? this.urlParser.decodeId(encodedId) : null;
      if (this.eventID) {
        const role = localStorage.getItem('userRole');
        this.loginRole = role;

        if (
          this.loginRole === 'superadmin' ||
          this.loginRole === 'teacher' ||
          this.loginRole === 'csquser' ||
          this.loginRole === 'csqadmin'
        ) {
          this.fetchViewEvent(this.eventID);
          this.fetchViewTable(this.eventID);
          this.fetchEventStatus().subscribe();
          this.getEngagementMetrics(this.eventID);
        }

        if (this.loginRole === 'student') {
          this.fetchStudentEventDetails();
        }

        if (
          this.loginRole === 'superadmin' ||
          this.loginRole === 'teacher' ||
          this.loginRole === 'csqadmin' ||
          this.loginRole === 'csquser'
        ) {
          this.listViewRoles.studentList.mediaConsent = true;
        }
      }
    });
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
    setTimeout(() => {
      if (
        this.eventID &&
        (this.loginRole === 'superadmin' ||
          this.loginRole === 'teacher' ||
          this.loginRole === 'csqadmin' ||
          this.loginRole === 'csquser')
      ) {
        this.fetchChartData(this.eventID);
      }
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.removeStatusDropdownClickListener();
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  }

  approveInterestedStudent(row: any): void {
    const dialogData: AlertDialogData = {
      title: 'Approve Student',
      message: `Are you sure you want to approve ${row['student name']}?`,
      okText: 'Approve',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });
    dialogRef.beforeClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.httpService
        .put<any>(
          `teacherevent/${this.eventID}/students/${row?.studentId}/review-interested`,
          { approve: true },
        )
        .subscribe({
          next: (response) => {
            if (response.status) {
              this.showSuccessToast(
                response.message || 'Student approved successfully.',
              );
              this.fetchViewTable(this.eventID);
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (err: any) => this.showErrorToast(err?.message),
        });
    });
  }

  declineInterestedStudent(row: any): void {
    const dialogData: AlertDialogData = {
      title: 'Decline Student',
      message: `Are you sure you want to decline ${row['student name']}?`,
      okText: 'Decline',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });
    dialogRef.beforeClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.httpService
        .put<any>(
          `teacherevent/${this.eventID}/students/${row?.studentId}/review-interested`,
          { approve: false },
        )
        .subscribe({
          next: (response) => {
            if (response.status) {
              this.showSuccessToast(response.message || 'Student declined.');
              this.fetchViewTable(this.eventID);
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (err: any) => this.showErrorToast(err?.message),
        });
    });
  }

  onDelete(school: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure want to delete ?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.beforeClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.httpService
        .delete<any>(
          `teacherevent/${this.eventID}/students/${school?.studentId}`,
        )
        .subscribe({
          next: (response) => {
            if (response.status) {
              this.fetchViewTable(this.eventID);
              this.showSuccessToast(response.message);
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (err) => {
            console.error('Failed to delete school:', err);
            this.showErrorToast(err?.message);
          },
        });
    });
  }

  fetchViewEvent(id: string): void {
    this.pageloading = true;
    if (!id) {
      this.pageloading = false;
      return;
    }
    this.httpService
      .get<APIResponse>(`events/${id}`)
      .pipe(
        finalize(() => {
          // Add small delay to ensure smooth transition
          this.loadingTimeout = setTimeout(() => {
            this.pageloading = false;
            this.cdr.detectChanges();
          }, 300);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.data && !Array.isArray(response.data)) {
            if (response.data) {
              this.eventData = {
                ...response.data,
              };
            }
          } else {
            this.eventData = null;
            this.showErrorToast('Event data not found');
          }
        },
        error: (err) => {
          console.error('Failed to load event details:', err);
          this.eventData = null;
          if (
            err?.status === 403 ||
            err?.message === 'You are not authorised to access this page.'
          ) {
            this.router.navigate(['/unauthorized']);
          } else {
            this.showErrorToast(err?.message);
          }
        },
      });
  }

  private fetchStudentEventDetails(): void {
    if (this.eventID) {
      this.isLoading = true;
      this.isFetchingStudentData = true;
      this.httpService
        .get<APIResponse>(`studentdashboard/${this.eventID}`)
        .subscribe({
          next: (response) => {
            if (response?.status) {
              this.eventData = response.data;
              this.isRegistered = this.eventData?.isRegistered;
              this.showConsentButton =
                this.eventData?.isMediaConsentParentApprovalRequired &&
                Number(this.eventData?.studentAge) < 16;
            } else {
              this.showErrorToast(response.message);
            }
            this.isLoading = false;
            this.isFetchingStudentData = false;
          },
          error: (error) => {
            this.isLoading = false;
            this.isFetchingStudentData = false;
            if (
              error?.message === 'You are not authorised to access this page.'
            ) {
              localStorage.removeItem('studentEvent');
              this.router.navigate(['/unauthorized']);
            } else {
              this.showErrorToast(error?.message);
            }
          },
        });
    }
  }

  fetchEventStatus() {
    return this.httpService.get<any>('events/event-statuses').pipe(
      tap({
        next: (response) => {
          if (Array.isArray(response.data)) {
            this.statusOptions = response.data.map((item: any) => ({
              label: item.name || item.label,
              value: item.id || item.value,
            }));
          } else {
            this.statusOptions = [];
            this.showErrorToast('Invalid status data format');
          }
        },
        error: (err) => {
          console.error('Failed to load event statuses:', err);
          this.statusOptions = [];
          this.showErrorToast(err?.message);
        },
      }),
    );
  }

  fetchViewTable(id: string): void {
    this.studentTabelLoading = true;
    this.httpService
      .get<APIResponse>(`events/${id}/registrations`)
      .pipe(
        finalize(() => {
          this.studentTabelLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          this.eventStudentList = Array.isArray(response?.data)
            ? response.data.map((item) => ({
                id: item.registrationId,
                'school name': item.schoolName || '',
                'student name': item.studentName || '',
                'approval status': item.teacherApprovalStatus
                  ? 'Approved'
                  : 'Rejected',
                attended: item.isAttended ?? false,
                'PD points': item.pdPoints ?? '-',
                'PD Points': item.pdPoints ?? '-',
                'media consent': item.isMediaConsentApproved
                  ? item?.isMediaConsentApproved
                  : 'Pending',
                notes: item.notes || '',
                adminStatus: item?.adminApprovalStatus,
                teacherStatus: item?.teacherApprovalStatus,
                status:
                  item.adminApprovalStatus &&
                  item?.registrationStatus == 'TeacherApproved'
                    ? 'Registered'
                    : item?.registrationStatus
                      ? item?.registrationStatus
                      : 'Pending',
                isDownloading: false,
                studentId: item.studentId,
                age: item.studentAge ?? 0,
              }))
            : [];
        },
        error: (err) => {
          this.showErrorToast(`Something went wrong. Please try again later.`);
        },
      });
  }

  getEngagementMetrics(id: string) {
    if (!id) {
      return;
    }
    this.httpService
      .get<APIResponse>(`events/${id}/engagement-metrics`)
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.engagementMetrics = response?.data ?? null;
          } else {
            console.error('Failed to load event details:');
          }
        },
        error: (err) => {
          console.error('Failed to load event details:', err);
          this.showErrorToast(err?.message);
        },
        complete: () => (this.loading = false),
      });
  }

  fetchChartData(eventId: string): void {
    this.chartLoading = true;
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    this.httpService
      .get<APIResponse>(`events/${eventId}/registration-over-time`)
      .pipe(
        finalize(() => {
          this.chartLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (
            response.data &&
            Array.isArray(response.data) &&
            response.data.length > 0
          ) {
            this.resChartData = response.data;
            this.updateChartWithRealData();
          } else {
            this.useDummyChartData();
          }
        },
        error: (error) => {
          console.error('Failed to load chart data:', error);
          this.useDummyChartData();
        },
      });
  }

  fetchViewEventGraph(id: string): void {
    if (!id) {
      return;
    }

    this.httpService
      .get<APIResponse>(`events/${id}/registration-over-time`)
      .subscribe({
        next: (response) => {
          if (
            response.data &&
            Array.isArray(response.data) &&
            response.data.length > 0
          ) {
            this.resChartData = response.data;
            this.updateChartWithRealData();
          } else {
            this.resChartData = null;
            this.useDummyChartData();
          }
        },
        error: (err) => {
          this.resChartData = null;
          this.useDummyChartData();
          this.showErrorToast(err?.message);
        },
        complete: () => (this.loading = false),
      });
  }

  updateEventStatus(newStatus: any): void {
    this.approvalStatusLoading = true;
    this.httpService
      .put<APIResponse>(
        `events/${this.eventData.eventId}/toggle-status?eventStatusId=${newStatus.value || newStatus}`,
        '',
      )
      .pipe(
        finalize(() => {
          this.approvalStatusLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.showSuccessToast(response?.message);
            this.fetchViewEvent(this.eventID);
            this.closeStatusDropdown();
          } else {
            this.showErrorToast(
              response.message ||
                'Something went wrong. Please try again later.',
            );
          }
        },
        error: (error) => {
          console.error('Status update error:', error?.message);
          this.showErrorToast(
            error?.message || 'Something went wrong. Please try again later.',
          );
        },
      });
  }

  getStatusClass(status: any): string {
    if (!status) return '';
    const normalizedStatus = String(status).trim().toLowerCase();

    switch (normalizedStatus) {
      case 'awaiting approval':
        return 'awaiting-status';

      case 'upcoming':
        return 'status-upcoming';

      case 'rejected':
        return 'rejected-status';

      case 'approved':
        return 'completed-text';

      case 'completed':
      case 'event completed':
      case 'eventcompleted':
      case 'already held':
      case 'alreadyheld':
        return 'completed-text';

      case 'ongoing':
      case 'on today':
        return 'status-on-today';

      case 'draft':
        return 'status-draft';

      case 'on hold':
      case 'onhold':
        return 'status-on-hold';

      case 'cancelled':
      case 'event cancelled':
      case 'eventcancelled':
      case 'deleted':
        return 'cancelled-text';

      default:
        return 'awaiting-status';
    }
  }

  getFocusAreaNames(): string {
    return (
      this.eventData?.focusAreas
        ?.map((area: any) => area.focusAreaName)
        .join(', ') || '-'
    );
  }

  getManagersNames(): string {
    return (
      this.eventData?.managers
        ?.map((manger: any) => manger.firstName + ' ' + manger.lastName)
        .join(', ') || '-'
    );
  }

  getMCCpartnersNames(): string {
    return (
      this.eventData?.mccPartners
        ?.map((manger: any) => manger.mccEventPartnerName)
        .join(', ') || '-'
    );
  }

  getStatusDisplayValue(statusValue: any): string {
    if (!statusValue) return 'Pending';

    const displayMap: Record<string, string> = {
      Rejected: 'Declined',
      TeacherRejected: 'Teacher Declined',
      AdminRejected: 'Declined',
    };

    if (displayMap[statusValue]) return displayMap[statusValue];

    const statusArray = this.statusOptions;
    const status = statusArray.find(
      (opt) =>
        opt.value === statusValue ||
        opt.label === statusValue ||
        opt.id === statusValue,
    );

    return status?.label || statusValue || 'Pending';
  }

  isPdf() {
    if (!this.eventData?.imagePaths[0]) return false;
    return this.eventData?.imagePaths[0].toLowerCase().endsWith('.pdf');
  }

  getEventImage(): boolean {
    if (this.eventData?.imagePaths && this.eventData.imagePaths.length > 0) {
      return true;
      // this.eventData.imagePaths[0];
    }
    return false;
  }

  async downloadFiles() {
    if (!this.eventData?.imagePaths?.length) {
      this.showErrorToast('Something went wrong');
      // this.updateRowDownloadingState(row.id, false);
      return;
    }

    let url = this.eventData?.imagePaths[0];
    console.log(url);
    if (this.isPdf()) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `student-evidence-document`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      this.showEventModal = this.eventData?.imagePaths[0];
    }
  }

  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.showStatusDropdown = !this.showStatusDropdown;
    if (this.showStatusDropdown) {
      this.addStatusDropdownClickListener();
    } else {
      this.removeStatusDropdownClickListener();
    }
    this.cdr.detectChanges();
  }

  closeEventModal() {
    this.showEventModal = false;
  }

  onToggleChange(event: { row: any; checked: boolean }): void {
    const { row, checked } = event;
    const originalState = row.attended;

    const dialogData: AlertDialogData = {
      title: 'Confirm attendance update',
      message: checked
        ? 'Do you want to mark this student as Attended?'
        : 'Do you want to mark this student as Not Attended?',
      okText: 'Yes',
      cancelText: 'No',
      icon: 'warning',
      submessage: '',
      cancelButtonColor: '#EF4A00',
      okButtonColor: '#5BD763',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      const payload = { isAttended: checked };

      this.httpService
        .put<APIResponse>(`teacherevent/${row?.id}/attendance`, payload)
        .subscribe({
          next: (response) => {
            if (response.status) {
              row.attended = checked;
              this.getEngagementMetrics(this.eventID);
              this.cdr.detectChanges();
              this.showSuccessToast(response.message);
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message || 'Failed to update attendance');
          },
        });
    });
  }

  onDownloadConsent(event: { row: any; id: boolean }): void {
    if (!this.listViewRoles.studentList.mediaConsent) {
      this.showErrorToast(
        'You do not have permission to download media consent',
      );
      return;
    }

    const { row } = event;

    this.downloadingRowId = row.id;
    this.downloadInProgress = true;
    this.updateRowDownloadingState(row.id, true);

    this.httpService
      .download(`events/registrations/${row?.id}/media-consent/pdf`)
      .pipe(
        finalize(() => {
          this.clearDownloadingState(row.id);
        }),
      )
      .subscribe({
        next: (res) => {
          const blob = res.body;
          if (!blob || blob.size === 0) {
            this.showErrorToast('Empty PDF received');
            return;
          }

          if (
            blob.type &&
            !blob.type.includes('pdf') &&
            !blob.type.includes('application/octet-stream')
          ) {
            this.showErrorToast('Invalid file format received');
            return;
          }

          const contentDisposition =
            res.headers.get('content-disposition') || '';
          const filename =
            this.extractFilename(contentDisposition) ||
            `media-consent-${row?.id}.pdf`;

          this.triggerDownload(blob, filename);
          this.showSuccessToast('Media Consent downloaded successfully!');
        },
        error: (err) => {
          console.error('Download error:', err);

          if (err.error && err.error instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const errorResponse = JSON.parse(reader.result as string);
                this.showErrorToast(
                  errorResponse?.message || 'Failed to download PDF',
                );
              } catch {
                this.showErrorToast(err?.message || 'Failed to download PDF');
              }
            };
            reader.readAsText(err.error);
          } else {
            this.showErrorToast(err?.message || 'Failed to download PDF');
          }
        },
      });
  }

  private clearDownloadingState(rowId?: string): void {
    this.downloadInProgress = false;
    if (rowId) {
      this.updateRowDownloadingState(rowId, false);
    }
    this.downloadingRowId = null;
  }

  private updateRowDownloadingState(
    rowId: string,
    isDownloading: boolean,
  ): void {
    const studentIndex = this.eventStudentList.findIndex(
      (item) => item.id === rowId,
    );
    if (studentIndex !== -1) {
      this.eventStudentList[studentIndex].isDownloading = isDownloading;
    }
  }

  private extractFilename(cd: string): string | null {
    if (!cd) return null;
    const filenameMatch = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/['"]/g, '');
    }
    return null;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  onNoteUpdate(event: any): void {
    const payload = {
      notes: event?.notes ?? '',
    };

    this.httpService
      .put<APIResponse>(`events/${event?.row?.id}/adminnotes`, payload)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.showSuccessToast(response?.message);
            this.fetchViewTable(this.eventID);
            this.getEngagementMetrics(this.eventID);
          } else {
            this.showErrorToast(response?.message);
          }
        },
        error: (error) => {
          console.error('Note update error:', error?.message);
          this.showErrorToast(
            error?.message || 'Something went wrong. Please try again later.',
          );
        },
      });
  }

  initializeChart(): void {
    if (!this.chartContainer?.nativeElement) {
      setTimeout(() => this.initializeChart(), 100);
      return;
    }
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    const chartData = this.chartData || [];
    const dataPoints = chartData.map((item) => item.registrations);
    const categories = chartData.map((item) => item.date);

    const options: ApexCharts.ApexOptions = {
      series: [
        {
          name: 'Registrations',
          data: dataPoints,
        },
      ],
      chart: {
        type: 'area',
        height: 250,
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent',
        fontFamily: 'inherit',
        animations: {
          enabled: true,
          speed: 800,
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth',
        width: 3,
        colors: ['#007BFF'],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.6,
          opacityTo: 0.1,
          stops: [0, 90, 100],
          colorStops: [
            {
              offset: 0,
              color: '#007BFF',
              opacity: 0.6,
            },
            {
              offset: 100,
              color: '#1e3a5f',
              opacity: 0.1,
            },
          ],
        },
      },
      grid: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        strokeDashArray: 5,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
        padding: { top: 0, right: 20, bottom: 0, left: 10 },
      },
      xaxis: {
        categories: categories,
        labels: {
          style: { colors: '#9CA3AF', fontSize: '12px' },
          rotate: 0,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          style: { colors: '#9CA3AF', fontSize: '12px', fontWeight: 500 },
          formatter: (value: number) => Math.round(value).toString(),
        },
        min: 0,
        forceNiceScale: true,
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        x: { show: true, format: 'dd MMM yyyy' },
        y: {
          formatter: (value: number) =>
            `${value} registration${value !== 1 ? 's' : ''}`,
        },
        style: { fontSize: '12px' },
      },
    };

    try {
      this.chart = new ApexCharts(this.chartContainer.nativeElement, options);
      this.chart
        .render()
        .then(() => {
          this.chartInitialized = true;
          this.chartLoading = false;
          this.cdr.detectChanges();
        })
        .catch((error) => {
          console.error('Chart rendering failed:', error);
          this.chartLoading = false;
          this.cdr.detectChanges();
        });
    } catch (error) {
      console.error('Chart initialization failed:', error);
      this.chartLoading = false;
      this.cdr.detectChanges();
    }
  }

  updateChartData(newData: { date: string; registrations: number }[]): void {
    this.chartData = newData;

    if (this.chart && this.chartInitialized) {
      try {
        this.chart.updateOptions({
          xaxis: { categories: this.chartData.map((item) => item.date) },
          yaxis: {
            min: 0,
            max:
              Math.max(...this.chartData.map((item) => item.registrations), 1) *
              1.1,
          },
        });

        this.chart.updateSeries([
          {
            name: 'Registrations',
            data: this.chartData.map((item) => item.registrations),
          },
        ]);
      } catch (error) {
        console.error('Failed to update chart:', error);
        this.chart.destroy();
        this.chart = null;
        this.initializeChart();
      }
    } else {
      this.initializeChart();
    }
  }

  updateChartWithRealData(): void {
    if (
      !this.resChartData ||
      !Array.isArray(this.resChartData) ||
      this.resChartData.length === 0
    ) {
      this.useDummyChartData();
      return;
    }

    const chartData = this.resChartData.map((item) => ({
      date: this.formatChartDate(item.date),
      registrations: item.registrations || 0,
    }));

    if (chartData.length === 1) {
      const singleDate = new Date(this.resChartData[0].date);
      const previousDate = new Date(singleDate);
      previousDate.setDate(previousDate.getDate() - 1);

      chartData.unshift({
        date: this.formatDateToChartFormat(previousDate),
        registrations: 0,
      });
    }

    this.updateChartData(chartData);
  }

  formatDateToChartFormat(date: Date): string {
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
    });
  }

  formatChartDate(dateString: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString.split('T')[0];
      }

      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString.split('T')[0];
    }
  }

  useDummyChartData(): void {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dummyData = [
      { date: this.formatDateToChartFormat(yesterday), registrations: 0 },
      { date: this.formatDateToChartFormat(today), registrations: 0 },
    ];

    this.updateChartData(dummyData);
  }

  openAddStudentDialog(
    teacherToEdit: APIResponse | undefined = undefined,
    isEdit = false,
    width = '50.5rem',
  ): void {
    const storedUserDetails = JSON.parse(
      localStorage.getItem('userDetails') || '{}',
    );
    const schoolID =
      (teacherToEdit as any)?.schools?.[0]?.schoolId ||
      (teacherToEdit as any)?.schoolId ||
      (teacherToEdit as any)?.schoolID ||
      storedUserDetails?.schoolId ||
      '';

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: {
        initialData: teacherToEdit,
        EditMode: isEdit,
        schoolID: schoolID,
        eventId: this.eventID,
        addStudent: true,
        ViewMode: false,
      },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    this.dialog
      .open(AddStudentComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.fetchViewTable(this.eventID);
          this.getEngagementMetrics(this.eventID);
        }
      });
  }

  openAssignStudentsDialog(): void {
    const dialogConfig: MatDialogConfig = {
      width: '40rem',
      maxWidth: '95vw',
      minWidth: '300px',
      data: { eventId: this.eventID, endpoint: 'TeacherEvent' },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };
    this.dialog
      .open(AssignStudentsDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.fetchViewTable(this.eventID);
          this.getEngagementMetrics(this.eventID);
        }
      });
  }

  goBack() {
    this.location.back();
  }

  onEdit(data: any): void {
    this.openAddDialog(data, true);
  }

  openAddDialog(
    schoolToEdit: any = null,
    isEdit: boolean = false,
    width: string = '54.5rem',
  ): void {
    const dialogRef = this.dialog.open(WorkExperienceComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: schoolToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchViewEvent(this.eventID);
      }
    });
  }

  private addStatusDropdownClickListener(): void {
    this.removeStatusDropdownClickListener();
    this.statusDropdownClickListener = () => {
      this.closeStatusDropdown();
    };
    setTimeout(() => {
      document.addEventListener('click', this.statusDropdownClickListener!);
    }, 0);
  }

  private removeStatusDropdownClickListener(): void {
    if (this.statusDropdownClickListener) {
      document.removeEventListener('click', this.statusDropdownClickListener);
      this.statusDropdownClickListener = null;
    }
  }

  closeStatusDropdown(): void {
    if (this.showStatusDropdown || this.showApprovalStatusDropdown) {
      this.showStatusDropdown = false;
      this.showApprovalStatusDropdown = false;
      this.removeStatusDropdownClickListener();
      this.cdr.markForCheck();
    }
  }

  openCopyLink(width = '50.5rem', initialData?: {}): void {
    const dialogConfig: MatDialogConfig<any> = {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: {
        initialData: {},
      },
      disableClose: true,
      autoFocus: false,
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      panelClass: 'blur-backdrop-dialog',
    };

    try {
      const dialogRef = this.dialog.open(
        EventLinkDialogComponent,
        dialogConfig,
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (result?.success) {
        }
      });
    } catch (error) {
      console.error('Error opening dialog:', error);
    }
  }

  addFeedBack(width = '50.5rem', initialData?: {}): void {
    const dialogConfig: MatDialogConfig<any> = {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: {
        initialData: {},
      },
      disableClose: true,
      autoFocus: false,
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      panelClass: 'blur-backdrop-dialog',
    };

    try {
      const dialogRef = this.dialog.open(
        AddFeedBackDialogComponent,
        dialogConfig,
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (result?.success) {
        }
      });
    } catch (error) {
      console.error('Error opening dialog:', error);
    }
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  exportStudentsToCSV(): void {
    if (!this.eventStudentList.length) return;

    const headers = [
      'School Name',
      'Student Name',
      'Status',
      'Attended',
      'PD Points',
      'Media Consent',
      'Notes',
    ];

    const rows = this.eventStudentList.map((s) => [
      s['school name'] ?? '',
      s['student name'] ?? '',
      s['status'] ?? '',
      s['attended'] === true ? 'Yes' : s['attended'] === false ? 'No' : '',
      s['PD Points'] ?? s['PD points'] ?? '',
      s['media consent'] ?? '',
      s['notes'] ?? '',
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const val = String(cell ?? '').replace(/"/g, '""');
            return `"${val}"`;
          })
          .join(','),
      )
      .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const eventName = (this.eventData?.eventName ?? 'event')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    this.triggerDownload(blob, `${eventName}_students.csv`);
  }
}
